import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import { DataTable, Driver } from 'remote/global';
import { Grid, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/utils/data/routes';

interface Props {
  drivers: {
    elements: Driver[];
    totalElements: number;
    pageNo: number;
    pageSize: number;
    pageCount: number;
  };
  loading: boolean;
  onPageChange?: (page: number) => void;
  currentPage: number;
  pageSize: number;
  totalElements: number;
}

function DriversTable({
  drivers,
  loading,
  onPageChange,
  currentPage,
  pageSize,
  totalElements,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: `${t('LogisticsView.driver.table.header.name')}`,
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => {
          return (
            <Typography
              sx={{
                fontSize: '14px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() =>
                navigate([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.DRIVERS, row.id].join('/'))
              }
            >
              {`${row.contactInfo?.firstName ?? ''} ${row.contactInfo?.lastName ?? ''}`.trim() ||
                ''}
            </Typography>
          );
        },
      },
      {
        field: 'status',
        headerName: `${t('FormFields.status.label')}`,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Typography
            sx={{
              fontSize: '14px',
            }}
          >
            {t(`DriverStatus.${row.status}`)}
          </Typography>
        ),
      },
      {
        field: 'phone',
        headerName: `${t('LogisticsView.driver.table.header.phone')}`,
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => row.contactInfo?.phone || '',
      },
      {
        field: 'email',
        headerName: `${t('FormFields.email.label')}`,
        flex: 1,
        minWidth: 240,
        valueGetter: ({ row }) => row.contactInfo?.email || '',
      },
      {
        field: 'assignedBus',
        headerName: `${t('DriversView.table.header.assignedBus')}`,
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography
            sx={{
              fontSize: '14px',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={() =>
              navigate([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET, row.vehicleId].join('/'))
            }
          >
            {row.vehicleLicensePlate || ''}
          </Typography>
        ),
      },

      {
        field: 'workLocation',
        headerName: `${t('DriversView.table.header.workLocation')}`,
        flex: 1,
        minWidth: 139,
        valueGetter: ({ row }) => row.vehicleAddress || '',
      },
      {
        field: 'languages',
        headerName: `${t('FormFields.languages.label')}`,
        flex: 1,
        minWidth: 139,
        renderCell: ({ row }) =>
          row.languages
            ?.map((language: string) => language.charAt(0).toUpperCase() + language.slice(1))
            .join(', ') || '',
      },
    ];
  }, [t, navigate]);

  return (
    <Grid item sx={{ p: { xs: 1, md: 2 } }}>
      <DataTable
        rows={drivers}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id.toString()}
        t={t}
        onPageChange={onPageChange}
        count={Math.ceil(totalElements / pageSize)}
        page={currentPage + 1}
      />
    </Grid>
  );
}

export default DriversTable;
